import { EventIds, ItemsPerPage, ItemsPerPageGuest } from 'constants/index';
import { withErrorBoundary } from 'error';
import React, { useRef } from 'react';
import { t } from '@lingui/macro';
import { useParams } from 'react-router-dom';
import { PackBySlugOrGuid } from 'apollo/pack/pack.gql';
import { ApolloError, useQuery } from '@apollo/client';
import PackDetailsCarousel from 'components/PackDetailsCarousel/PackDetailsCarousel';
import { PageBody } from 'components/PageLayout';
import {
    Pack,
    PackBySlugOrGuidQuery,
    PackBySlugOrGuidQueryVariables,
} from 'types/generated/graphql';
import { getSanitizeSlug } from 'helpers/getSanitizeSlug';
import { useScrollToElement } from 'hooks';
import { useTakenDownPack } from 'hooks/useTakenDown/useTakenDown';
import TakeDownInfo from 'components/TakeDownInfo/TakeDownInfo';
import PageHeaderForPackOrRepack from 'components/PageHeaderForPackOrRepack/PageHeaderForPackOrRepack';
import useUpdateErrorBoundary, {
    itemErrorGuard,
} from 'hooks/useUpdateErrorBoundary/useUpdateErrorBoundary';
import SearchProvider from 'controllers/SearchController/SearchContext';
import DetailsView from 'components/DetailsView/DetailsView';
import packQueries from 'apollo/pack/pack';
import Filters from 'components/Filters/Filters';
import SearchQuery from 'controllers/SearchController/components/SearchQuery/SearchQuery';
import { PageBreadcrumbs } from 'components/PageBreadcrumbs/PageBreadcrumbs';
import { RoutePaths } from 'types/enums';
import { usePrerenderComponentsRequired } from 'hooks/usePrerenderComponentsRequired/usePrerenderComponentsRequired';
import { FreemiumGatingBanner } from 'components/FreemiumGatingBanner/FreemiumGatingBanner';
import { useFreemiumGatingContext } from 'contexts/FreemiumGatingContext/FreemiumGatingContext';
import { DropdownSortingTypes } from 'components/DetailsView/components/useSortingOptionItem';
import { ArtistBanner } from 'components/ArtistBanner/ArtistBanner';

const usePackDetails = (
    slug?: string,
): {
    pack: Pack | undefined;
    loading: boolean;
    error: ApolloError | undefined;
} => {
    const { data, loading, error } = useQuery<
        PackBySlugOrGuidQuery,
        PackBySlugOrGuidQueryVariables
    >(PackBySlugOrGuid, {
        skip: !slug,
        variables: { packSlugOrGuid: getSanitizeSlug(slug) },
    });

    return { pack: data?.pack, loading, error };
};

const sortingOptions = [
    DropdownSortingTypes.Trending,
    DropdownSortingTypes.Recent,
    DropdownSortingTypes.NameAsc,
    DropdownSortingTypes.NameDesc,
];

const PackDetails: React.FC = () => {
    usePrerenderComponentsRequired({
        componentNames: ['SamplesPane'],
    });
    const { slug } = useParams<{ slug: string }>();
    const { pack, loading, error } = usePackDetails(slug);
    const { isTakenDown } = useTakenDownPack(pack);
    const { isAppGated } = useFreemiumGatingContext();

    const itemsPerPage = isAppGated ? ItemsPerPageGuest : ItemsPerPage;

    const pageBodyRef = useRef<HTMLDivElement>(null);
    const { scrollToElement: handlePageChanged } =
        useScrollToElement(pageBodyRef);

    const didError = useUpdateErrorBoundary({
        getDidError: itemErrorGuard<Pack>(loading, error, pack),
        message: `PackDetail render null`,
        eventId: EventIds.PackDetailsRenderNull,
        error,
    });

    if (didError) {
        return null;
    }

    const queries = packQueries(pack && pack.guid);

    return (
        <>
            <PageHeaderForPackOrRepack
                item={pack}
                isTakenDown={isTakenDown}
                isLoading={loading || !pack}
            />
            <PageBody ref={pageBodyRef}>
                {isTakenDown ? (
                    <TakeDownInfo />
                ) : (
                    <SearchProvider samplesPerPage={itemsPerPage}>
                        <SearchQuery samplesQueryConfig={queries.samples} />
                        <Filters defaultPlaceholder={t`Search within pack`}>
                            <Filters.SearchBar />
                            <Filters.SuggestionsPills />
                        </Filters>
                        <DetailsView>
                            <DetailsView.DropdownSorting
                                sortingOptions={sortingOptions}
                            />
                            <DetailsView.SamplesPane
                                onPageChanged={handlePageChanged}
                            />
                        </DetailsView>
                    </SearchProvider>
                )}
                {pack && <PackDetailsCarousel item={pack} />}
                <ArtistBanner />
                {isAppGated && <FreemiumGatingBanner />}
            </PageBody>
            <PageBreadcrumbs
                breadcrumbs={[
                    { route: RoutePaths.ForYou, label: t`Home` },
                    { route: RoutePaths.Browse, label: t`Browse` },
                    {
                        route: RoutePaths.Labels,
                        label: t`Labels`,
                    },
                    {
                        route: RoutePaths.Labels,
                        wildcard: pack?.label?.slug,
                        label: pack?.label?.name,
                    },
                    {
                        route: RoutePaths.Packs,
                        wildcard: pack?.slug,
                        label: pack?.name,
                        isCurrent: true,
                    },
                ]}
            />
        </>
    );
};

PackDetails.displayName = 'PackDetails';

export default withErrorBoundary(PackDetails, 'page');
